import { defineStore } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { domainToProfileIdOptionsMap } from '@/contentprofile/domain_content_profiles'

const DISABLED_THREADS_DOMAIN_NAME_PATTERNS = ['Clark County School District', 'CPS Network', 'Chicago Public Schools (SD 299) (IL)','Jovita Idar Elementary (IL)','Alain Locke Charter School (IL)','Catalyst Circle Rock Elementary School (IL)','Catalyst Schools (IL)','Distinctive Schools (IL)','Great Lakes Academy Charter School (Chicago IL)','KIPP Chicago (IL)','LEARN Charter School Network (IL)','Legacy Elementary Charter School (IL)','Nancy B. Jefferson Alternative High School (Chicago IL)','Perspectives Charter Schools (IL)','Providence Englewood Charter School (IL)','ReGeneration Schools (IL)','Youth Connection Charter School (Chicago IL)','Oscar Mayer Magnet School (IL)','King Academy of Social Justice (CPS)(IL)','Ogden International High School (IL)','Bulls College Prep (IL)','Gary Comer Middle School (IL)','UChicago Charter Woodlawn Campus (University of Chicago Charter School (IL))']
const DISABLED_REPORT_UPLOAD_DOMAIN_NAME_PATTERNS = ['(NYC', 'Mott Haven Academy Charter School (NY)', 'New York City Geographic District #20', 'St. Dominic TORCH (NY)', 'United Charter High Schools (NY)']

export const useVisibilityStore = defineStore('visibility', {
  state: () => ({
    betaMode: false,
    editorMode: false,
    iodMode: null,
    effectsMode: null,
    shareMode: null,
    threadsMode: null,
    reportUploadMode: null,
    disabledThreadsDomains: DISABLED_THREADS_DOMAIN_NAME_PATTERNS,
    disabledReportUploadDomains: DISABLED_REPORT_UPLOAD_DOMAIN_NAME_PATTERNS,
  }),

  getters: {
    editorVisibility(state) {
      const userStore = useUserStore()
      return state.editorMode && userStore.isSuperAdmin
    },
    betaVisibility(state) {
      const userStore = useUserStore()
      return state.betaMode && userStore.isAuthenticated
    },
    selVisibility() {
      const contentProfile = useContentProfileStore()
      const profileId = contentProfile.profileId ||  ''
      return !profileId.endsWith('_no_SEL')
    },
    iodVisibility(state) {
      if (state.iodMode !== null) return state.iodMode
      const contentProfile = useContentProfileStore()
      // check if the content profile is one that doesn't allow IOD feature
      const noIODProfiles = ['nevada-no-iod', 'no-iod-test']
      return noIODProfiles.indexOf(contentProfile.profileId) === -1
    },
    effectsVisibility(state) {
      if (state.effectsMode !== null) return state.effectsMode
      // check if the content profile is one that has effect statements in PLW
      const contentProfile = useContentProfileStore()
      const effectsProfiles = ['nevada-no-iod']
      return effectsProfiles.indexOf(contentProfile.profileId) > -1
    },
    shareVisibility(state) {
      if (state.shareMode !== null) return state.shareMode
      const contentProfile = useContentProfileStore()
      // check if the content profile is one that doesn't allow share feature
      const noShareProfiles = ['florida_no_share_no_SEL']
      return noShareProfiles.indexOf(contentProfile.profileId) === -1
    },
    threadsVisibility(state) {
      if (state.threadsMode !== null) return state.threadsMode
      const userStore = useUserStore()
      const contentProfile = useContentProfileStore()
      const isAuthenticated = userStore.isAuthenticated
      if (!isAuthenticated) return false

      const userDomainName = userStore.domainInfo?.name || ''
      const belongsToDisabledDomain = state.disabledThreadsDomains.some(domain => userDomainName.includes(domain))

      const domainFlags = userStore.domainInfo?.flags || []
      const hasNoThreadsFlag = domainFlags.includes('no_threads')

      const profileId = contentProfile.profileId || ''
      const hasNoAIProfile = profileId.endsWith('no-ai')

      return !belongsToDisabledDomain && !hasNoThreadsFlag && !hasNoAIProfile
    },
    reportUploadVisibility(state) {
      if (state.reportUploadMode !== null) return state.reportUploadMode
      const userStore = useUserStore()
      const isAuthenticated = userStore.isAuthenticated
      if (!isAuthenticated) return false

      const userDomainName = userStore.domainInfo?.name || ''
      const belongsToDisabledDomain = state.disabledReportUploadDomains.some(domain => userDomainName.includes(domain))

      return !belongsToDisabledDomain
    },
    canChangeProfile() {
      const userStore = useUserStore()
      return userStore.domains.some((domain) => new Set(Object.keys(domainToProfileIdOptionsMap)).has(domain.uuid))
    },
    aiFeatureVisibility() {
      const contentProfile = useContentProfileStore()
      const profileId = contentProfile.profileId || ''
      const noAIProfiles = ['new-york_no-ai']
      return noAIProfiles.indexOf(profileId) === -1
    },
  },

  actions: {
    toggleBetaMode(flag) {
      this.betaMode = flag
    },
    toggleEditorMode(flag) {
      this.editorMode = flag
    },
    toggleIodMode(flag) {
      this.iodMode = flag
    },
    toggleEffectsMode(flag) {
      this.effectsMode = flag
    },
    toggleShareMode(flag) {
      this.shareMode = flag
    },
    toggleThreadsMode(flag) {
      this.threadsMode = flag
    },
  }
})
